import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { Observable, of, tap } from "rxjs";
import {
  CreateTestDto,
  FilterTestDto,
  ResponseTestDto,
  UpdateTestDto,
} from "../../../api/models";
import { BookingStatusType } from "../../../enums";
import { TestsApiService } from "../../api/healthcare/tests-api.service";
import { CachedServiceBase } from "../../base/cached-service.base";

@Injectable({
  providedIn: "root",
})
export class CachedTestsService extends CachedServiceBase<ResponseTestDto> {
  /** The tests API service */
  private readonly _testsApiService = inject(TestsApiService);

  // =============================================================================
  // DB FILTERS (trigger backend calls)
  // =============================================================================
  /** Patient ID filter - triggers DB call when changed */
  private readonly _patientId: WritableSignal<string | null> = signal(null);

  /** Clinic ID filter - triggers DB call when changed */
  private readonly _clinicId: WritableSignal<string | null> = signal(null);

  /** Status filter - triggers DB call when changed */
  private readonly _status: WritableSignal<string | undefined> =
    signal(undefined);

  /** Phase ID filter - triggers DB call when changed */
  private readonly _phaseId: WritableSignal<string | null> = signal(null);

  // =============================================================================
  // CLIENT-SIDE FILTERS (live filtering)
  // =============================================================================

  /** Title filter for live search */
  public readonly titleFilter: WritableSignal<string> = signal("");

  /** Test code filter for live search */
  public readonly codeFilter: WritableSignal<string> = signal("");

  /** Test clinic name filter for live search */
  public readonly clinicFilter: WritableSignal<string> = signal("");

  /** Booking date filter for live search */
  public readonly bookingDateFilter: WritableSignal<string> = signal("");

  /** Patient first name filter for live search */
  public readonly firstNameFilter: WritableSignal<string> = signal("");

  /** Patient last name filter for live search */
  public readonly lastNameFilter: WritableSignal<string> = signal("");

  // =============================================================================
  // CONSTRUCTOR & EFFECTS
  // =============================================================================

  constructor() {
    super();

    // Effect: reset pagination when client filters change
    effect(() => {
      this.titleFilter();
      this.bookingDateFilter();
      this.codeFilter();
      this.clinicFilter();
      this.firstNameFilter();
      this.lastNameFilter();
      // Reset to first page when client filters change
      this.resetPagination();
    });
  }

  // =============================================================================
  // IMPLEMENT ABSTRACT METHODS
  // =============================================================================

  /**
   * Apply client-side filters to backend data
   */
  protected getFilteredData(): Signal<ResponseTestDto[]> {
    return computed(() => {
      const allData = this.data();
      const title = this.titleFilter().toLowerCase().trim();
      const bookingDate = this.bookingDateFilter().trim();
      const firstName = this.firstNameFilter().toLowerCase().trim();
      const lastName = this.lastNameFilter().toLowerCase().trim();

      if (!title && !bookingDate && !firstName && !lastName) return allData;

      return allData.filter((item) => {
        const matchesTitle =
          !title || item.title?.toLowerCase().includes(title);
        const matchesBookingDate =
          !bookingDate || item.bookingDate?.includes(bookingDate);
        const matchesFirstName =
          !firstName || item.patientFirstName?.toLowerCase().includes(firstName);
        const matchesLastName =
          !lastName || item.patientLastName?.toLowerCase().includes(lastName);

        return matchesTitle && matchesBookingDate && matchesFirstName && matchesLastName;
      });
    });
  }

  public refresh(): void {
    const patientId = this._patientId();
    const clinicId = this._clinicId();
    const status = this._status();
    const phaseId = this._phaseId();
    if ((patientId || clinicId) && status) {
      this._fetchFromBackend(patientId, clinicId, status, phaseId);
    }
  }

  public reset(): void {
    this._patientId.set(null);
    this._clinicId.set(null);
    this._status.set(undefined);
    this._phaseId.set(null);
    this.titleFilter.set("");
    this.bookingDateFilter.set("");
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
    this.clearData();
  }

  // =============================================================================
  // COMPUTED SIGNALS
  // =============================================================================

  /** Signal to track if there are active text filters */
  public readonly hasActiveFilters: Signal<boolean> = computed(() => {
    const title = this.titleFilter().trim();
    const bookingDate = this.bookingDateFilter().trim();
    const code = this.codeFilter().trim();
    const clinic = this.clinicFilter().trim();
    const firstName = this.firstNameFilter().trim();
    const lastName = this.lastNameFilter().trim();
    return !!(title || bookingDate || code || clinic || firstName || lastName);
  });

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  /**
   * Fetch data from backend based on patientId/clinicId, status and phaseId
   */
  private _fetchFromBackend(
    patientId: string | null,
    clinicId: string | null,
    status: string  | undefined,
    phaseId: string | null,
  ): void {
    const filterData: Partial<FilterTestDto> = { status };
    if (patientId) filterData.patientId = patientId;
    if (clinicId) filterData.clinicId = clinicId;
    if (phaseId) filterData.phaseId = phaseId;

    this._testsApiService
      .filter$(filterData)
      .pipe(
        tap((allResults) => {
          // Apply client-side filtering and deduplication
          const processedResults = this._processTestResults(allResults, status);
          this.updateData(processedResults);
        })
      )
      .subscribe();
  }

  /**
   * Process test results: filter by status and deduplicate.
   * This is necessary due to a backend limitation where phaseId filtering
   * doesn't respect status filters.
   * @param results Raw results from backend
   * @param status Optional status filter (can contain multiple statuses separated by /)
   * @returns Processed and deduplicated results
   */
  private _processTestResults(
    results: ResponseTestDto[],
    status: string | undefined
  ): ResponseTestDto[] {
    // Step 1: Filter by status if needed
    // Backend bug workaround: when phaseId is used, backend returns all tests regardless of status
    let filtered = results;
    if (status && this._phaseId()) {
      const allowedStatuses = new Set(
        status.split('/').map(s => s.trim().toLowerCase())
      );
      filtered = results.filter(test => 
        allowedStatuses.has(test.status?.toLowerCase() || '')
      );
    }
    
    // Step 2: Deduplicate by ID (in case backend returns duplicates)
    const uniqueMap = new Map<string, ResponseTestDto>();
    filtered.forEach(test => {
      if (test.id && !uniqueMap.has(test.id)) {
        uniqueMap.set(test.id, test);
      }
    });
    
    return Array.from(uniqueMap.values());
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /**
   * Set patient ID (effect will trigger backend call)
   */
  public setPatientId(patientId: string): void {
    this._patientId.set(patientId);
    this._clinicId.set(null); // Clear clinic ID when setting patient ID
    this.refresh();
  }

  /**
   * Set clinic ID (effect will trigger backend call)
   */
  public setClinicId(clinicId: string): void {
    this._clinicId.set(clinicId);
    this._patientId.set(null); // Clear patient ID when setting clinic ID
    this.refresh();
  }

  /**
   * Set status (effect will trigger backend call)
   */
  public setStatus(status: string): void {
    this._status.set(status);
    this.refresh();
  }

  /**
   * Set phase ID (triggers backend call)
   */
  public setPhaseId(phaseId: string | null): void {
    this._phaseId.set(phaseId);
    this.refresh();
  }

  /**
   * Initializes the filters all at once with the provided payload to avoid the retrigger of the backend call.
   * @param payload the payload containing patientId, clinicId, status, and optionally phaseId.
   */
  public initializeFilters(payload: { patientId: string | null; clinicId: string | null; status: BookingStatusType; phaseId?: string | null }): void {
    this._patientId.set(payload.patientId);     
    this._clinicId.set(payload.clinicId);       
    this._status.set(payload.status);
    if (payload.phaseId !== undefined) {
      this._phaseId.set(payload.phaseId);
    }           
    this.refresh();
  }

  /**
   * Clear text filters (title and bookingDate)
   */
  public clearTextFilters(): void {
    this.titleFilter.set("");
    this.bookingDateFilter.set("");
    this.codeFilter.set("");
    this.clinicFilter.set("");
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public nextPage(): void {
    this.goToNextPage();
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public previousPage(): void {
    this.goToPreviousPage();
  }

  /**
   * Legacy compatibility - get current page index
   */
  public currentPageIndex(): number {
    return this.paginationInfo().currentPage;
  }

  /**
   * Legacy compatibility - has next page
   */
  public hasNextPage(): boolean {
    return this.paginationInfo().hasNextPage;
  }

  /**
   * Legacy compatibility - has previous page
   */
  public hasPreviousPage(): boolean {
    return this.paginationInfo().hasPreviousPage;
  }

  /**
   * Legacy compatibility - should show pagination
   */
  public shouldShowPagination(): boolean {
    return this.paginationInfo().shouldShowPagination;
  }

  /**
   * Legacy compatibility - get total pages
   */
  public totalPages(): number {
    return this.paginationInfo().totalPages;
  }

  // =============================================================================
  // CRUD OPERATIONS
  // =============================================================================

  /**
   * Creates a new test
   * @param body The DTO containing test creation data
   * @returns Observable of the created test
   */
  public create$(body: CreateTestDto): Observable<ResponseTestDto> {
    return this._testsApiService.create$(body).pipe(tap(() => this.refresh()));
  }

  /**
   * Updates an existing test
   * @param id The id of the test
   * @param body The DTO containing test update data
   * @returns Observable of the updated test
   */
  public update$(
    id: string,
    body: UpdateTestDto,
  ): Observable<ResponseTestDto> {
    return this._testsApiService
      .update$(id, body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Deletes a test by id
   * @param id The id of the test
   * @returns Observable of operation result
   */
  public delete$(id: string): Observable<void> {
    return this._testsApiService
      .delete$(id)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Retrieves a single test
   * @param id The id of the test to retrieve
   * @returns Observable of the test
   */
  public findOne$(id: string): Observable<ResponseTestDto> {
    // Search in backend data cache
    const allData = this.data();
    const cached = allData.find((test) => test.id === id);

    if (cached) {
      return of(cached);
    }

    return this._testsApiService.findOne$(id);
  }

  // =============================================================================
  // LEGACY COMPATIBILITY FOR EXISTING COMPONENTS
  // =============================================================================

  /**
   * Legacy compatibility - filtered data property
   */
  public readonly filteredData: Signal<ResponseTestDto[]> = this.getFilteredData();
}
